import { ModuleLabel } from '../module-label';
import { Dialog, DialogHandle } from './dialog';
import { Button } from "primereact/button";
import { ModuleDto } from "../../general/dtos";
import { ProgressSpinner } from "primereact/progressspinner";
import { HtmlViewer } from "../htmlViewer";
import { useEffect, useRef, useState } from "react";
import { toggleFullscreen } from "../../general/browser";
import { InstructionsDialog } from "./view-module-instructions-dialog";

type ViewModuleDialogProps = {
  visible: boolean;
  contentLoading: boolean;
  fullModule: ModuleDto | null;
  onClose: () => void;
  onMessage?: (kind: string, data: any) => Promise<void>;
  isReviewMode?: boolean;
};

export function ViewModuleDialog({
  visible,
  contentLoading,
  fullModule,
  onClose,
  onMessage,
  isReviewMode = false
}: ViewModuleDialogProps) {
  const dialogRef = useRef<DialogHandle>(null);
  const dialogContainerRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [instructionsVisible, setInstructionsVisible] = useState<boolean>(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const onShow = () => {
    // PrimeReact renders the dialog DOM AFTER visible=true
    const container = dialogRef.current?.getElement() ?? null;
    dialogContainerRef.current = container;
  };

  const header = (
    <div className="flex align-items-center justify-content-between w-full">
        <span className="text-lg font-semibold"><ModuleLabel name={fullModule?.name ?? "Module details"} managed={fullModule?.is_managed} /></span>

        <Button
            icon={isFullscreen ? "pi pi-window-minimize" : "pi pi-window-maximize"}
            className="p-button-text p-button-rounded"
            onClick={() => {
                if (dialogContainerRef.current) {
                    toggleFullscreen(dialogContainerRef.current);
                }
            }}
        />
    </div>
  );

  return (<>
    <Dialog
        ref={dialogRef}
        header={header}
        visible={visible}
        onShow={onShow}
        onHide={() => onClose()}
        modal
        draggable={false}
        baseZIndex={10000}
        style={{ width: "95vw",
            maxWidth: "2100px",
            height: "99vh",            // gives the dialog a frame
            display: "flex",
            flexDirection: "column"
        }}
        breakpoints={{
            "1400px": "95vw",       // large laptops
            "1200px": "95vw",       // smaller laptops
            "960px": "95vw",        // tablets
            "640px": "100vw"        // phones
        }}
        contentStyle={{
            flex: 1,                   // content area expands
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"         // prevents double scrollbars
        }}
        transitionOptions={{ classNames: "p-dialog", timeout: 300 }}
        >
            {contentLoading && (<><div className="flex flex-column justify-content-center align-items-center h-screen gap-3">
                <ProgressSpinner />
                <span className="text-xl text-primary">Loading module content...</span>
            </div></>)}

            {!contentLoading && (
                <div className="entity-list-container">
                    <div className="entity-list-wrapper" style={{ isolation: 'isolate' }}>
                        <HtmlViewer
                            htmlContent={fullModule?.content ?? ''}
                            theme={fullModule?.theme ?? ''}
                            instructions={fullModule?.instructions ?? ''}
                            onMessage={(kind, data) => { onMessage?.(kind, data); }}
                            selectedMode={"preview"}
                            readOnly={true}
                            inputsReadOnly={isReviewMode}
                            hideControls={true}
                            sandboxStyles={true}
                        />
                    </div>
                </div>
            )}

            {!contentLoading && (<>
                <div className="flex align-items-center mt-4" style={{ marginTop: "auto" }} >
                    <div className="flex flex-1">
                        {(fullModule?.instructions ?? '').length > 0 && (<>
                            <Button
                                label="Instructions"
                                icon="pi pi-info-circle"
                                onClick={() => setInstructionsVisible(true)}
                                className={ "p-button-secondary" }
                            />
                        </>)}
                    </div>

                    <div className="flex justify-content-end flex-1">
                        <Button
                            label="Close"
                            icon="pi pi-times"
                            onClick={() => onClose()}
                            className="p-button-secondary"
                        />
                    </div>
                </div>
                <InstructionsDialog parent={dialogContainerRef.current} visible={instructionsVisible} onHide={() => setInstructionsVisible(false) } instructions={fullModule?.instructions ?? ''} />
            </>)}
    </Dialog>
    </>
  );
}
